import { isRef, ref, nextTick, computed } from 'vue';
import type { Ref } from 'vue';
import { KEYBOARD_KEY } from '@/constants/keyboard';
import { checkParentNodeExistClassName } from '@/views/goods/common';
import { v4 as uuidv4 } from 'uuid';
import { isFunction } from 'lodash';

// 建议typeRef取column.field，用于通过tableRef和field左右滚动定位列
type TypeStringArray = string[];
interface paramsType {
  tableRef: Ref<Recordable>;
  readonly?: boolean | Ref<boolean>;
  // tableData: Recordable[] | Ref<Recordable[]>;
  typeList: TypeStringArray | Ref<TypeStringArray>;
  excludedRefType?: TypeStringArray | Ref<TypeStringArray>;
  getPrevDataLineIndex?: (i: number) => number;
  getNextDataLineIndex?: (i: number) => number;
  getLeftRefType?: (i: string) => string;
  getRightRefType?: (i: string) => string;
  checkRefType?: (ev: {
    row: Recordable;
    inputType: string;
    inputRef?: Recordable;
  }) => boolean;
}

const { ENTER, ARROW_DOWN, ARROW_UP, ARROW_LEFT, ARROW_RIGHT } = KEYBOARD_KEY;
// 用于给vxe-table中有输入框时，在输入框中按下enter自动聚焦到下一行对应列的输入框
const useTableGetNextLineFocus = ({
  tableRef, // 表格ref，通过ref去滚动列和获取表格数据
  readonly = false, // 为true时不使用快捷键
  // tableData, // 表格数据
  typeList, // ref的所有输入框类型，表格一列一个类型
  excludedRefType, // 左右快捷键排除的Ref
  getPrevDataLineIndex, // 用于获取上一行索引的自定义方法，不传则默认获取上一行
  getNextDataLineIndex, // 用于获取下一行索引的自定义方法，不传则默认获取下一行
  getLeftRefType, // 用于获取上一个ref类型的自定义方法，不传则默认向左获取上一个ref
  getRightRefType, // 用于获取下一个ref类型的自定义方法，不传则默认向右获取下一个ref
  checkRefType, // 校验inputType是否合法（目前用不到，而是通过getInputRefByRow(row, nextRefType)判断如果没有inputRef则找下一个）
}: paramsType) => {
  // 转为Ref
  const data: Ref<Recordable[]> = computed(() => {
    return tableRef.value?.data ?? [];
  });
  const disableKeydwon: Ref<boolean> = isRef(readonly)
    ? readonly
    : ref(readonly ?? false);
  const refTypeList: Ref<TypeStringArray> = isRef(typeList)
    ? typeList
    : ref(typeList ?? []);
  const excludedRefTypeList: Ref<TypeStringArray> = isRef(excludedRefType)
    ? excludedRefType
    : ref(excludedRefType ?? []);
  const dynamicType = ref<TypeStringArray>([]); // 需要动态加进来的ref类型
  const inputRefs = ref<
    Record<string, Record<TypeStringArray[number], Recordable>>
  >({}); // 存当前所有输入框的ref

  // 当前按下的inputRefType
  const curKeyDownInputType = ref<string>();
  const curKeyDownRowIndex = ref<number>();
  // 往data添加数据是给row初始化uuid和ref
  const initInputFocusRef = (row: Recordable) => {
    row.uuid = uuidv4(); // 设置uuid
    if (disableKeydwon.value) return;
    initRefByTypeList(allTypeList.value, row.uuid);
  };
  // 初始化ref
  const initRefByTypeList = (list: TypeStringArray, uuid: string) => {
    if (!inputRefs.value?.[uuid]) {
      inputRefs.value[uuid] = {};
    }
    list.forEach((key: TypeStringArray[number]) => {
      inputRefs.value[uuid][key] = ref(null);
    });
  };
  // 设置对应行的ref
  const setInputFocusRef = (
    el: any,
    uuid: string,
    inputType: TypeStringArray[number]
  ) => {
    if (!inputRefs.value?.[uuid]) {
      inputRefs.value[uuid] = {};
    }
    // 父节点中是fixed，并且当前列不是固定列（则说明这一列是fixed表格并且未被渲染，属于是不可见列）
    if (
      el?.$parent?.fixedType &&
      !el?.$parent?.fixedColumn?.find(
        (item: Recordable) => item.field === inputType
      )
    )
      return;
    inputRefs.value[uuid][inputType] = el;
  };
  // 获取inputRef
  const getInputRefByuuidType = (
    uuid: string,
    inputType: TypeStringArray[number]
  ) => {
    if (disableKeydwon.value) return undefined;
    return inputRefs.value?.[uuid]?.[inputType];
  };
  // 删除某个uuid（某行）的所有ref
  const removeInputRefByuuid = (uuid: string) => {
    if (disableKeydwon.value) return;
    delete inputRefs.value?.[uuid];
  };
  // 重置所有输入框的ref
  const resetInputFocusRef = () => {
    inputRefs.value = {};
  };
  // 给输入框绑定上ref，注意绑定的组件需要是column默认插槽default下的根节点(或者根节点的子元素，不可以在下一层了)
  const bindInputFocusRef = (
    el: any,
    row: Recordable,
    inputType: TypeStringArray[number]
  ) => {
    if (disableKeydwon.value) return;
    // 当vxe-table中存在fixed列时会渲染多个table用于实现固定列，所以可以通过vxe-body--column的类名fixed--hidden判断当前单元格是否是隐藏状态，隐藏状态不加ref是为了让输入框ref唯一
    const defaultParentNode = el?.$el?.parentNode?.parentNode;
    const isExistHiddenClass = checkParentNodeExistClassName(
      defaultParentNode,
      'fixed--hidden',
      3
    );
    if (isExistHiddenClass) return;
    // 不隐藏时
    if (!row?.uuid) {
      initInputFocusRef(row);
    }
    setInputFocusRef(el, row.uuid, inputType);
    // if (
    //   !defaultParentNode?.className ||
    //   !defaultParentNode?.parentNode?.className ||
    //   !defaultParentNode?.parentNode?.parentNode?.className
    // )
    //   return;
    // const className = `${defaultParentNode?.className} ${defaultParentNode?.parentNode?.className} ${defaultParentNode?.parentNode?.parentNode?.className}`;
    // // .vxe-body--column.fixed--hidden .vxe-cell .输入框（所以要有两层parentNode）
    // if (typeof className === 'string') {
    //   if (className && !className.includes('fixed--hidden')) {
    //     // 不隐藏时
    //     if (!row?.uuid) {
    //       initInputFocusRef(row);
    //     }
    //     setInputFocusRef(el, row.uuid, inputType);
    //   }
    // }
  };
  // 键盘按下的处理事件(inputType请保持取column.field)，用于通过ref左右滚动列
  const handleKeyDownInput = (e: any, rowIndex: number, inputType: string) => {
    if (!disableKeydwon.value) {
      if (e.key === ENTER) {
        if (
          !e.shiftKey && // 没有按下shift
          !e.ctrlKey && // 没有按下ctrl
          !e.altKey && // 没有按下alt
          !e.metaKey // 没有按下meta
        ) {
          e.preventDefault();
          e.stopPropagation();
          curKeyDownInputType.value = inputType;
          curKeyDownRowIndex.value = rowIndex;
          handleKeyDownByEnter(rowIndex, inputType);
        }
      } else if (
        e.altKey &&
        [ARROW_DOWN, ARROW_UP, ARROW_LEFT, ARROW_RIGHT].includes(e.key)
      ) {
        curKeyDownInputType.value = inputType;
        curKeyDownRowIndex.value = rowIndex;
        if (e.key === ARROW_DOWN) {
          e.preventDefault();
          e.stopPropagation();
          handleKeyDownByEnter(rowIndex, inputType);
        } else if (e.key === ARROW_UP) {
          e.preventDefault();
          e.stopPropagation();
          handleKeyDownByUp(rowIndex, inputType);
        } else if (e.key === ARROW_LEFT) {
          if (!usedRefTypeList.value.length) return;
          e.preventDefault();
          e.stopPropagation();
          handleKeyDownByLeft(rowIndex, inputType);
        } else if (e.key === ARROW_RIGHT) {
          if (!usedRefTypeList.value.length) return;
          e.preventDefault();
          e.stopPropagation();
          handleKeyDownByRight(rowIndex, inputType);
        }
      }
    }
  };
  const handleKeyDownByEnter = (rowIndex: number, inputType: string) => {
    // enter | alt + ↓
    nextTick(() => {
      const nextRow = data.value[defaultGetNextIndex(rowIndex, inputType)];
      if (nextRow?.uuid) {
        inputRefs.value?.[nextRow.uuid as string]?.[inputType]?.focus();
        setRowHighlight(nextRow);
      }
    });
  };
  const handleKeyDownByUp = (rowIndex: number, inputType: string) => {
    // alt + ↑
    nextTick(() => {
      const nextRow = data.value[defaultGetPrevIndex(rowIndex, inputType)];
      if (nextRow?.uuid) {
        inputRefs.value?.[nextRow.uuid as string]?.[inputType]?.focus();
        setRowHighlight(nextRow);
      }
    });
  };
  const handleKeyDownByLeft = (rowIndex: number, inputType: string) => {
    // alt + ←
    nextTick(() => {
      const nextRefType = defaultGetLeftRefType(inputType, rowIndex);
      const currentRow = data.value[rowIndex];
      if (currentRow?.uuid && nextRefType) {
        inputRefs.value?.[currentRow.uuid as string]?.[nextRefType]?.focus();
      }
      if (isRef(tableRef) && nextRefType) {
        tableRef?.value?.scrollToColumn(nextRefType);
        setColumnHighlight(nextRefType);
      }
    });
  };
  const handleKeyDownByRight = (rowIndex: number, inputType: string) => {
    // alt + →
    nextTick(() => {
      const nextRefType = defaultGetRightRefType(inputType, rowIndex);
      const currentRow = data.value[rowIndex];
      if (currentRow?.uuid && nextRefType) {
        inputRefs.value?.[currentRow.uuid as string]?.[nextRefType]?.focus();
      }
      if (isRef(tableRef) && nextRefType) {
        tableRef?.value?.scrollToColumn(nextRefType);
        setColumnHighlight(nextRefType);
      }
    });
  };

  // 获取对应inputRef的输入框el
  const getInputRefByRow = (row: Recordable, inputType: string) => {
    return row.uuid
      ? inputRefs.value?.[row.uuid as string]?.[inputType]
      : undefined;
  };

  // 校验inputRef是否合法可用
  const checkRefTypeFun = (row: Recordable, inputType: string) => {
    const inputRef = getInputRefByRow(row, inputType);
    if (
      isFunction(checkRefType) &&
      !checkRefType({
        row,
        inputType,
        inputRef,
      })
    ) {
      return true;
    }
    return !inputRef;
  };
  // 默认获取上一行索引
  const defaultGetPrevIndex = (rowIndex: number, inputType: string) => {
    if (isFunction(getPrevDataLineIndex)) {
      return getPrevDataLineIndex(rowIndex);
    }
    const prevIndex = rowIndex - 1 < 0 ? data.value.length - 1 : rowIndex - 1;
    if (curKeyDownRowIndex.value === prevIndex) return prevIndex;
    const row = data.value[prevIndex];
    if (checkRefTypeFun(row, inputType)) {
      return defaultGetPrevIndex(rowIndex - 1, inputType);
    }
    return prevIndex;
  };
  // 默认获取下一行索引
  const defaultGetNextIndex = (rowIndex: number, inputType: string) => {
    if (isFunction(getNextDataLineIndex)) {
      return getNextDataLineIndex(rowIndex);
    }
    const nextIndex = rowIndex + 1 > data.value.length - 1 ? 0 : rowIndex + 1;
    if (curKeyDownRowIndex.value === nextIndex) return nextIndex;
    const row = data.value[nextIndex];
    if (checkRefTypeFun(row, inputType)) {
      return defaultGetNextIndex(rowIndex + 1, inputType);
    }
    return nextIndex;
  };
  // 默认向左获取上一个ref
  const defaultGetLeftRefType = (inputType: string, rowIndex: number) => {
    if (isFunction(getLeftRefType)) {
      return getLeftRefType(inputType);
    }
    const getPrevRefType = (type: string) => {
      const prevIndex =
        usedRefTypeList.value.findIndex((e: string) => e === type) - 1;
      return usedRefTypeList.value[
        prevIndex < 0 ? usedRefTypeList.value.length - 1 : prevIndex
      ];
    };
    const row = data.value[rowIndex];
    const prevRefType = getPrevRefType(inputType);
    if (curKeyDownInputType.value === prevRefType) return prevRefType;
    if (checkRefTypeFun(row, prevRefType)) {
      return defaultGetLeftRefType(prevRefType, rowIndex);
    }
    return prevRefType;
  };
  // 默认向右获取下一个ref
  const defaultGetRightRefType = (inputType: string, rowIndex: number) => {
    if (isFunction(getRightRefType)) {
      return getRightRefType(inputType);
    }

    const getNextRefType = (type: string) => {
      const nextIndex =
        usedRefTypeList.value.findIndex((e: string) => e === type) + 1;
      return usedRefTypeList.value[
        nextIndex > usedRefTypeList.value.length - 1 ? 0 : nextIndex
      ];
    };
    const row = data.value[rowIndex];
    const nextRefType = getNextRefType(inputType);
    if (curKeyDownInputType.value === nextRefType) return nextRefType;
    if (checkRefTypeFun(row, nextRefType)) {
      return defaultGetRightRefType(nextRefType, rowIndex);
    }
    return nextRefType;
  };
  // 重置动态ref类型列表
  const resetDynamicType = (list: TypeStringArray) => {
    dynamicType.value = list;
    data.value.forEach((row: Recordable) => {
      if (!row.uuid) return;
      initRefByTypeList(dynamicType.value, row.uuid);
    });
  };
  // 聚焦文本框时全选字符，并高亮当前列（高亮当前行需要对应vxe-table设置:row-config="{ isCurrent: true }"）
  const focusInput = (e: any, field?: any) => {
    e?.currentTarget?.select();
    setColumnHighlight(field);
  };
  const setColumnHighlight = (field?: any) => {
    if (!field) return;
    tableRef.value?.setCurrentColumn(field);
  };
  const setRowHighlight = (row?: any) => {
    if (!row) return;
    tableRef.value?.setCurrentRow(row);
  };
  // 计算所有ref类型
  const allTypeList = computed(() => {
    return refTypeList.value.concat(dynamicType.value);
  });
  // 排除快捷键不需要的ref
  const usedRefTypeList = computed(() => {
    if (!excludedRefTypeList.value?.length) return allTypeList.value;
    return allTypeList.value.filter(
      (r: string) => !excludedRefTypeList.value.includes(r)
    );
  });
  return {
    data,
    inputRefs,
    initInputFocusRef, // 在添加data数据前调用，初始化uuid和ref
    resetInputFocusRef, // 重置所有ref
    bindInputFocusRef, // 给每个输入框绑定ref
    handleKeyDownInput, // 处理输入框enter按下事件
    resetDynamicType, // 用于动态列的情况可以动态设置ref列类型
    getInputRefByuuidType, // 获取指定input的ref
    removeInputRefByuuid, // 删除某个uuid（某行）的所有ref
    focusInput, // 聚焦文本框时全选字符，并高亮当前列
  };
};

export default useTableGetNextLineFocus;
